import type { TreeNode } from '@commenda/kakashi';

import type { IIndirectTaxFilingV2LiabilityReportFiling } from '@commenda/api/indirect-tax/filings';
import { SalesTaxFilingStatusEnum } from '@prisma/client';

import { IndirectTaxRegionHelper } from '../../common';

import type { ContributionLiabilityReportTableRow } from '../types';

import { IndirectTaxFilingReturnSummaryHelper } from './IndirectTaxFilingReturnSummaryHelper';
import { IndirectTaxFilingsHelper } from './IndirectTaxFilingsHelper';

interface LiabilityReportAmount {
  currency: string;
  amount: number;
}

interface LiabilityReportCurrencyTotal {
  currency: string;
  totalTaxDue: number | null;
}

/**
 * Liability report formatting, CSV export, and DataTable narrowing helpers for the
 * tax liability report modal.
 */
export class IndirectTaxLiabilityReportHelper {
  static getTaxDueByCurrency(
    rows: readonly LiabilityReportAmount[]
  ): LiabilityReportCurrencyTotal[] {
    const totals = new Map<string, number | null>();
    rows.forEach(({ currency, amount }) => {
      // Unknown currencies cannot be combined into a meaningful total.
      totals.set(
        currency,
        currency ? (totals.get(currency) ?? 0) + amount : null
      );
    });
    return Array.from(totals, ([currency, totalTaxDue]) => ({
      currency,
      totalTaxDue,
    })).sort((left, right) => left.currency.localeCompare(right.currency));
  }

  static formatLiabilityJurisdictionDisplay(raw: string): string {
    return IndirectTaxFilingsHelper.formatJurisdictionName(raw);
  }

  static formatTaxPeriodRange(startIso: string, endIso: string): string {
    const start = new Date(startIso);
    const end = new Date(endIso);
    // Period dates are date-only values parsed as UTC midnight; read and format
    // in UTC so users behind UTC don't see the period slip back (PLAT-2537).
    const opts: Intl.DateTimeFormatOptions = {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    };
    if (
      start.getUTCFullYear() === end.getUTCFullYear() &&
      start.getUTCMonth() === end.getUTCMonth()
    ) {
      return start.toLocaleDateString('en-US', opts);
    }
    return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString(
      'en-US',
      opts
    )}`;
  }

  /**
   * Expandable rows receive `TreeNode<T>` from DataTable; liability rows carry filing
   * fields at runtime — narrow once per callback site.
   */
  static contributionRowFromExpandableTreeNode(
    row: TreeNode<ContributionLiabilityReportTableRow>
  ): ContributionLiabilityReportTableRow {
    return row as unknown as ContributionLiabilityReportTableRow;
  }

  /**
   * Liability report API returns Prisma-backed status strings; `mapApiStatus` expects
   * `SalesTaxFilingStatusEnum` — same contract as the filings dashboard.
   */
  static mapLiabilityReportFilingStatusForUI(
    filingStatus: IIndirectTaxFilingV2LiabilityReportFiling['filingStatus']
  ) {
    return IndirectTaxFilingsHelper.mapApiStatus(
      filingStatus as SalesTaxFilingStatusEnum
    );
  }

  private static escapeCsvCell(cell: unknown): string {
    return `"${String(cell).replace(/"/g, '""')}"`;
  }

  private static csvFromRows(headers: string[], dataRows: string[][]): string {
    return [headers, ...dataRows]
      .map((row) => row.map((cell) => this.escapeCsvCell(cell)).join(','))
      .join('\n');
  }

  /** CSV for the breakdown tab (sales-tax-style columns). */
  static buildBreakdownTabCsvContent(
    filingsWithBreakdown: ReadonlyArray<IIndirectTaxFilingV2LiabilityReportFiling>,
    currencyByFilingId: ReadonlyMap<string, string>
  ): string {
    const headers = [
      'Jurisdiction',
      'Return',
      'Tax period',
      'Due date',
      'Transactions',
      'Currency',
      'Taxable amount',
      'Non-taxable amount',
      'Tax due',
      'Status',
    ];
    const dataRows = filingsWithBreakdown.map((filing) => {
      // Non-null: caller includes only filings with breakdown data.
      const breakdown = filing.breakdown!;
      return [
        IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
          filing.jurisdiction
        ),
        filing.formTitle,
        IndirectTaxLiabilityReportHelper.formatTaxPeriodRange(
          filing.taxPeriodStartDate,
          filing.taxPeriodEndDate
        ),
        IndirectTaxFilingsHelper.formatDueDate(filing.dueDate),
        String(breakdown.transactionsCount),
        currencyByFilingId.get(filing.filingId) ?? '',
        breakdown.estimatedTaxableAmount.toFixed(2),
        breakdown.estimatedNonTaxableAmount.toFixed(2),
        breakdown.estimatedTaxCollected.toFixed(2),
        filing.filingStatus,
      ];
    });
    return IndirectTaxLiabilityReportHelper.csvFromRows(headers, dataRows);
  }

  /** CSV for the contribution tab (VAT lines). */
  static buildContributionTabCsvContent(
    filingsWithContributions: ReadonlyArray<IIndirectTaxFilingV2LiabilityReportFiling>,
    currencyByFilingId: ReadonlyMap<string, string>
  ): string {
    const headers = [
      'Jurisdiction',
      'Return',
      'Tax period start',
      'Tax period end',
      'Due date',
      'Filing status',
      'Filing through jurisdiction',
      'Box name',
      'Currency',
      'Amount',
    ];
    const dataRows: string[][] = [];
    for (const filing of filingsWithContributions) {
      const periodStart = filing.taxPeriodStartDate.slice(0, 10);
      const periodEnd = filing.taxPeriodEndDate.slice(0, 10);
      const sortedContributions = [...(filing.contributions ?? [])].sort(
        (left, right) => left.boxOrder - right.boxOrder
      );
      if (sortedContributions.length === 0) {
        dataRows.push([
          IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
            filing.jurisdiction
          ),
          filing.formTitle,
          periodStart,
          periodEnd,
          IndirectTaxFilingsHelper.formatDueDate(filing.dueDate),
          filing.filingStatus,
          '',
          '',
          currencyByFilingId.get(filing.filingId) ?? '',
          '',
        ]);
      } else {
        for (const contribution of sortedContributions) {
          const amountMajorUnits =
            IndirectTaxFilingReturnSummaryHelper.contributionValueToMajor(
              contribution
            );
          const filingThroughJurisdictionLabel =
            contribution.filingThroughJurisdiction != null &&
            contribution.filingThroughJurisdiction !== ''
              ? IndirectTaxRegionHelper.getCountryName(
                  contribution.filingThroughJurisdiction
                )
              : '';
          dataRows.push([
            IndirectTaxLiabilityReportHelper.formatLiabilityJurisdictionDisplay(
              filing.jurisdiction
            ),
            filing.formTitle,
            periodStart,
            periodEnd,
            IndirectTaxFilingsHelper.formatDueDate(filing.dueDate),
            filing.filingStatus,
            filingThroughJurisdictionLabel,
            contribution.boxName,
            currencyByFilingId.get(filing.filingId) ?? '',
            amountMajorUnits.toFixed(2),
          ]);
        }
      }
    }
    return IndirectTaxLiabilityReportHelper.csvFromRows(headers, dataRows);
  }
}
